import { z } from 'zod';

import { ALERT_PATTERN_MAX_LENGTH, ALERT_PATTERNS_MAX_COUNT } from '../constants/alert-patterns.js';
import { ANALOG_BANDWIDTH_OPTIONS, DIGITAL_BANDWIDTH_OPTIONS } from '../constants/suppressions.js';
import { t } from '../i18n/validation.js';
import { broadbandSuppressionModeSchema } from '../types/broadband-suppression.js';
import { DeviceType } from '../types/device-type.js';
import { SignalType } from '../types/signal-type.js';
import { SuppressionLogic } from '../types/suppression-logic.js';
import { SuppressionMode } from '../types/suppression-mode.js';
import type { Tag } from '../types/tag.js';

/**
 * A number, or a string that spells one. Nothing else.
 *
 * This used to be `z.coerce.number()`, which is far more permissive than it reads: `Number('')` and
 * `Number('   ')` are `0`, `Number(true)` is `1`, `Number([])` is `0`, and `Number(null)` is `0`.
 * Every one of those passed `.refine(Number.isFinite)`, because `0` and `1` are perfectly finite —
 * so an empty coordinate field validated cleanly and was submitted as latitude `0`, a real position
 * in the Gulf of Guinea that nothing downstream can tell from a deliberate one.
 *
 * The UI reaches this now. `optionalNumberField.setValueAs` maps a cleared field to `null` — the
 * sentinel that survives JSON.stringify and so actually removes a stored value — which is why the
 * four location fields below are `.nullable()` as well as `.optional()`. An empty string is still
 * rejected, and must stay rejected: that is the input that used to be stored as `0`.
 *
 * `undefined` never reaches the preprocessor — `.optional()` short-circuits it — so optional fields
 * keep behaving exactly as before.
 */
const finiteNumber = (message: string) =>
  z.preprocess(
    (value) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length === 0 ? Number.NaN : Number(trimmed);
      }
      return Number.NaN;
    },
    z.number({ error: message }).refine(Number.isFinite, message),
  );

const MAX_DEVICE_TAGS = 50;
export const MIN_DETECTION_LIFETIME_SECONDS = 90;

const alertPatternsInvalid = t('validation.deviceSettings.alertPatternsInvalid');

/** Same normalization as zahrada's `normalizeAlertPatterns`: trim, lowercase, drop empties, dedupe. */
export const normalizeAlertPattern = (pattern: string): string => pattern.trim().toLowerCase();

const normalizeAlertPatterns = (patterns: readonly string[]): string[] =>
  Array.from(new Set(patterns.map(normalizeAlertPattern).filter((pattern) => pattern.length > 0)));

// Limits apply to the normalized list, as on the server, and report at the list path so the form
// renders one message for the field.
const alertPatternsSchema = z.preprocess(
  (value) =>
    Array.isArray(value) && value.every((pattern) => typeof pattern === 'string')
      ? normalizeAlertPatterns(value)
      : value,
  z
    .array(z.string({ error: alertPatternsInvalid }), { error: alertPatternsInvalid })
    .superRefine((patterns, ctx) => {
      if (patterns.length > ALERT_PATTERNS_MAX_COUNT) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.deviceSettings.alertPatternsTooMany', {
            max: ALERT_PATTERNS_MAX_COUNT,
          }),
        });
      } else if (patterns.some((pattern) => pattern.length > ALERT_PATTERN_MAX_LENGTH)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation.deviceSettings.alertPatternTooLong', {
            max: ALERT_PATTERN_MAX_LENGTH,
          }),
        });
      }
    }),
);

const tagSchema = z.object({
  id: z.string(),
  name: z
    .string()
    .trim()
    .min(1, t('validation.tag.nameRequired'))
    .max(64, t('validation.tag.nameMaxLength')),
});

export const deviceSettingsSchema = z
  .object({
    deviceType: z.nativeEnum(DeviceType, {
      error: t('validation.deviceSettings.selectDeviceType'),
    }),
    suppressionMode: z.nativeEnum(SuppressionMode, {
      error: t('validation.deviceSettings.selectSuppressionMode'),
    }),
    suppressionLogic: z.nativeEnum(SuppressionLogic, {
      error: t('validation.deviceSettings.selectSuppressionLogic'),
    }),
    multiChannelsSuppress: finiteNumber(t('validation.provideValidNumber')).refine(
      (value: number) => value >= 0,
      {
        message: t('validation.deviceSettings.multiChannelsSuppressMin'),
      },
    ),
    maxSuppressionTime: finiteNumber(t('validation.deviceSettings.maxSuppressionTimeInvalid'))
      .refine((value: number) => value > 0, {
        message: t('validation.deviceSettings.maxSuppressionTimeMin'),
      })
      .optional(),
    defaultAnalogSuppressionBandwidth: finiteNumber(
      t('validation.deviceSettings.defaultAnalogSuppressionBandwidthInvalid'),
    ).refine((value: number) => ANALOG_BANDWIDTH_OPTIONS.includes(value), {
      message: t('validation.deviceSettings.defaultAnalogSuppressionBandwidthMin', {
        options: ANALOG_BANDWIDTH_OPTIONS.join(', '),
      }),
    }),
    defaultDigitalSuppressionBandwidth: finiteNumber(
      t('validation.deviceSettings.defaultDigitalSuppressionBandwidthInvalid'),
    ).refine((value: number) => DIGITAL_BANDWIDTH_OPTIONS.includes(value), {
      message: t('validation.deviceSettings.defaultDigitalSuppressionBandwidthMin', {
        options: DIGITAL_BANDWIDTH_OPTIONS.join(', '),
      }),
    }),
    defaultAnalogMultiFrequencySuppressionBandwidth: finiteNumber(
      t('validation.deviceSettings.defaultAnalogMultiFrequencySuppressionBandwidthInvalid'),
    )
      .refine((value: number) => ANALOG_BANDWIDTH_OPTIONS.includes(value), {
        message: t('validation.deviceSettings.defaultAnalogMultiFrequencySuppressionBandwidthMin', {
          options: ANALOG_BANDWIDTH_OPTIONS.join(', '),
        }),
      })
      .optional(),
    defaultDigitalMultiFrequencySuppressionBandwidth: finiteNumber(
      t('validation.deviceSettings.defaultDigitalMultiFrequencySuppressionBandwidthInvalid'),
    )
      .refine((value: number) => DIGITAL_BANDWIDTH_OPTIONS.includes(value), {
        message: t(
          'validation.deviceSettings.defaultDigitalMultiFrequencySuppressionBandwidthMin',
          {
            options: DIGITAL_BANDWIDTH_OPTIONS.join(', '),
          },
        ),
      })
      .optional(),
    defaultMultiFrequencySuppressionSignalType: z
      .nativeEnum(SignalType, {
        error: t('validation.deviceSettings.defaultMultiFrequencySuppressionSignalTypeInvalid'),
      })
      .optional(),
    defaultBroadbandSuppressionMode: broadbandSuppressionModeSchema,
    suppressionTime: finiteNumber(t('validation.provideValidNumber')).refine(
      (value: number) => value > 0,
      {
        message: t('validation.deviceSettings.suppressionTimeMin'),
      },
    ),
    suppressionMaxCount: finiteNumber(t('validation.provideValidNumber')).refine(
      (value: number) => value > 0,
      {
        message: t('validation.deviceSettings.suppressionMaxCountMin'),
      },
    ),
    suppressionInitialDelay: finiteNumber(
      t('validation.deviceSettings.suppressionInitialDelayInvalid'),
    ).refine((value: number) => value >= 0, {
      message: t('validation.deviceSettings.suppressionInitialDelayMin'),
    }),
    suppressionMinDetectionsCount: finiteNumber(
      t('validation.deviceSettings.suppressionMinDetectionsInvalid'),
    )
      .refine((value: number) => value > 0, {
        message: t('validation.deviceSettings.suppressionMinDetectionsMin'),
      })
      .refine((value: number) => value <= 20, {
        message: t('validation.deviceSettings.suppressionMinDetectionsMax'),
      }),
    suppressionMinSnr: finiteNumber(t('validation.deviceSettings.suppressionMinSnrInvalid'))
      .refine((value: number) => value > 0, {
        message: t('validation.deviceSettings.suppressionMinSnrMin'),
      })
      .refine((value: number) => value <= 30, {
        message: t('validation.deviceSettings.suppressionMinSnrMax'),
      }),
    azimuth: finiteNumber(t('validation.deviceSettings.azimuthInvalid'))
      .refine((value: number) => value >= 0, {
        message: t('validation.deviceSettings.azimuthMin'),
      })
      .refine((value: number) => value <= 359.99, {
        message: t('validation.deviceSettings.azimuthMax'),
      })
      .optional()
      .nullable(),
    elevation: finiteNumber(t('validation.deviceSettings.elevationInvalid'))
      .refine((value: number) => value >= 0, {
        message: t('validation.deviceSettings.elevationMin'),
      })
      .optional()
      .nullable(),
    latitude: finiteNumber(t('validation.deviceSettings.latitudeInvalid'))
      .refine((value: number) => value >= -90, {
        message: t('validation.deviceSettings.latitudeMin'),
      })
      .refine((value: number) => value <= 90, {
        message: t('validation.deviceSettings.latitudeMax'),
      })
      .optional()
      .nullable(),
    longitude: finiteNumber(t('validation.deviceSettings.longitudeInvalid'))
      .refine((value: number) => value >= -180, {
        message: t('validation.deviceSettings.longitudeMin'),
      })
      .refine((value: number) => value <= 180, {
        message: t('validation.deviceSettings.longitudeMax'),
      })
      .optional()
      .nullable(),
    detectionLifetime: finiteNumber(t('validation.deviceSettings.detectionLifetimeInvalid')).refine(
      (value: number) => value > MIN_DETECTION_LIFETIME_SECONDS,
      {
        message: t('validation.deviceSettings.detectionLifetimeMin'),
      },
    ),
    minAllowedFrequency: finiteNumber(t('validation.deviceSettings.minFrequencyInvalid')).refine(
      (value: number) => value > 0,
      {
        message: t('validation.deviceSettings.minFrequencyMin'),
      },
    ),
    maxAllowedFrequency: finiteNumber(t('validation.deviceSettings.maxFrequencyInvalid')).refine(
      (value: number) => value > 0,
      {
        message: t('validation.deviceSettings.maxFrequencyMin'),
      },
    ),
    suppressionAttemptDelay: finiteNumber(
      t('validation.deviceSettings.suppressionAttemptDelayInvalid'),
    ).refine((value: number) => value >= 0, {
      message: t('validation.deviceSettings.suppressionAttemptDelayMin'),
    }),
    txGainDecreaseLevel: finiteNumber(t('validation.deviceSettings.txGainInvalid'))
      .refine((value: number) => value >= 0, {
        message: t('validation.deviceSettings.txGainMin'),
      })
      .refine((value: number) => value < 60, {
        message: t('validation.deviceSettings.txGainMax'),
      }),
    friendOrFoeMinCalculationTime: finiteNumber(t('validation.deviceSettings.fofMinTimeInvalid'))
      .refine((value: number) => value > 0, {
        message: t('validation.deviceSettings.fofMinTimeMin'),
      })
      .refine((value: number) => value < 3600, {
        message: t('validation.deviceSettings.fofMinTimeMax'),
      }),
    friendOrFoeMaxCalculationTime: finiteNumber(t('validation.deviceSettings.fofMaxTimeInvalid'))
      .refine((value: number) => value > 0, {
        message: t('validation.deviceSettings.fofMaxTimeMin'),
      })
      .refine((value: number) => value < 3600, {
        message: t('validation.deviceSettings.fofMaxTimeMax'),
      }),
    tags: z
      .array(tagSchema)
      .max(MAX_DEVICE_TAGS, t('validation.maxTags', { max: MAX_DEVICE_TAGS }))
      .transform((tags: Tag[]) => tags.map((tag) => ({ ...tag, name: tag.name.trim() }))),
    alertPatterns: alertPatternsSchema,
    jumperMode: z.boolean().optional(),
    sapientEnabled: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.minAllowedFrequency >= data.maxAllowedFrequency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxAllowedFrequency'],
        message: t('validation.deviceSettings.maxFrequencyGreaterThanMin'),
      });
    }
  });

export type DeviceSettingsFormValues = z.infer<typeof deviceSettingsSchema>;
